<script lang="ts" setup>
import Link1 from '@/components/Button/Link1.vue'
import Slider1 from '@/components/Slider/Slider1.vue'
import { useArticleStore } from '@/stores'
import { type SliderList } from '@/utils/types'
import useSeo from './seo'

const articleStore = useArticleStore()
if (articleStore.blogCategories.length === 0) {
  await articleStore.fetchArticleCategories('blogCategories', {
    className: 'articleBlog'
  })
}
const blogCategories = computed(() => articleStore.blogCategories)
const imgLists: Ref<Array<SliderList>> = ref([])
const blogLink: Ref<string> = ref('')
const isSlideNext: Ref<boolean> = ref(true)
const refSlideLeft = ref()
const isLoading = ref(false)

blogCategories.value.forEach((o: any, i: number) => {
  if (i === 0) {
    const slug = o.seo?.meta_slug || o.id
    blogLink.value = `/blog/${slug}`
  }
  const imgList: SliderList = {
    pic: o.image?.link,
    title: o.title,
    link: o.url
  }
  imgLists.value.push(imgList)
})

const sliderTimer: Ref<number> = ref(4000)
const currentIndex: Ref<number> = ref(0)

const handleSlideChange = (index: number) => {
  currentIndex.value = index
  const slug = blogCategories.value[index]?.seo?.meta_slug || blogCategories.value[index]?.id
  blogLink.value = `/blog/${slug}`
}

const handleSlideGoto = (index: number) => {
  refSlideLeft.value.slideGoTo(index)
  currentIndex.value = index
}

onMounted(() => {
  isLoading.value = true
})

useSeo()
</script>

<template>
  <section class="blog" v-if="isLoading">
    <div class="blog__left">
      <div class="blog__left__inner">
        <Slider1 ref="refSlideLeft" :lists="imgLists" :timer="sliderTimer" :isGoNext="isSlideNext" direction="left"
          @update:slidechange="handleSlideChange" />
      </div>
    </div>
    <div class="blog__right">
      <div class="blog__right__top">
        <!-- <div class="blog__right__subtitle">blog</div>
        <div class="blog__right__title">品牌部落格</div> -->
        <Intro class="blog__right__intro" subtitle="blog" :data="{
          title: '品牌部落格'
        }" :h1="true" />
        <Link1 :to="blogLink" class="blog__right__top__link" color="gray-3">去看看</Link1>
      </div>
      <div class="blog__catrgory">
        <div class="blog__catrgory__title">類別</div>
        <ul class="blog__catrgory__lists">
          <template v-for="(item, index) in blogCategories" :key="index">
            <li :class="{ active: currentIndex === index }" @click="handleSlideGoto(index)">
              {{ item.subject }}
            </li>
          </template>
        </ul>
      </div>
      <div class="blog__right__link">
        <Link1 :to="blogLink" color="gray-3" :key="blogLink">去看看</Link1>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.blog {
  @include grid;

  @include max-media(767) {
    grid-template-columns: repeat(4, 1fr);
  }

  &__left {
    grid-column: 1 / span 6;

    @include max-media(767) {
      grid-column: 1 / span 4;
      margin-bottom: toRem(30);
    }

    &__inner {
      position: relative;
      width: 100%;
      padding-top: calc(100% - 5px);
      overflow: hidden;
    }

    img {
      display: block;
      width: 100%;
    }
  }

  &__right {
    grid-column: 7 / span 6;

    @include max-media(767) {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      column-gap: toRem(24);
      grid-column: 1 / span 4;
    }

    &__top {
      text-align-last: left;

      @include max-media(767) {
        grid-column: 1 / span 2;
      }

      &__link {
        @include min-media(768) {
          display: none;
        }
      }
    }

    &__subtitle {
      font-family: $font-TextaAlt;
      font-size: toRem(135);
      font-weight: 500;
      color: $color-gray-3;
      letter-spacing: toRem(-3.16);
      line-height: 1;

      @include max-media(767) {
        font-size: toRem(80);
      }
    }

    &__title {
      font-size: toRem(28);
      font-weight: 500;
    }

    &__link {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 12px;

      @include max-media(767) {
        display: none;
      }
    }
  }

  &__catrgory {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    column-gap: toRem(24);
    padding-top: toRem(120);

    @include max-media(767) {
      grid-template-columns: repeat(2, 1fr);
      grid-column: 3 / span 2;
    }

    &__title,
    &__lists {
      grid-column: 3 / span 2;
    }

    &__title {
      margin-bottom: toRem(18);
      font-size: toRem(16);
      color: $color-gray-1;
    }

    &__lists {
      font-size: toRem(22);
      color: $color-gray-1;

      @include max-media(480) {
        font-size: toRem(16);
      }

      li {
        position: relative;
        opacity: 0.5;
        margin-bottom: toRem(10);
        cursor: pointer;

        &::before {
          content: '';
          position: absolute;
          left: toRem(-20);
          top: 50%;
          margin-top: toRem(-3.5);
          width: toRem(7);
          height: toRem(7);
          background-color: $color-brand-3;
          opacity: 0;
        }

        &.active {
          opacity: 1;

          &::before {
            opacity: 1;
          }
        }
      }
    }
  }
}
</style>
<style lang="scss">
.blog {
  &__right {
    &__intro {
      .intro {
        &__title {
          @include max-media(767) {
            font-size: toRem(24);
            margin-bottom: toRem(50);
          }
        }
      }
    }
  }
}
</style>
